/**
 * Comprehensive flow validation before execution
 * Checks for cycles, unreachable nodes, invalid configurations, etc.
 */

export class FlowValidator {
  constructor() {
    this.errors = [];
    this.warnings = [];
  }

  /**
   * Validate flow structure comprehensively
   */
  validate(flow) {
    this.errors = [];
    this.warnings = [];

    if (!flow || typeof flow !== 'object') {
      this.errors.push('Invalid flow structure');
      return this.getResult();
    }

    const { nodes = [], edges = [] } = flow;

    // Basic structure validation
    this._validateBasicStructure(nodes, edges);
    
    // Check for cycles
    this._detectCycles(nodes, edges);
    
    // Check for unreachable nodes
    this._findUnreachableNodes(nodes, edges);
    
    // Validate node configurations
    this._validateNodeConfigurations(nodes);
    
    // Check edge validity
    this._validateEdges(nodes, edges);

    return this.getResult();
  }

  _validateBasicStructure(nodes, edges) {
    if (!Array.isArray(nodes)) {
      this.errors.push('Nodes must be an array');
      return;
    }

    if (!Array.isArray(edges)) {
      this.errors.push('Edges must be an array');
      return;
    }

    if (nodes.length > 1000) {
      this.errors.push('Too many nodes (max 1000)');
    }

    if (edges.length > 2000) {
      this.errors.push('Too many edges (max 2000)');
    }

    // Check for duplicate node IDs
    const nodeIds = new Set();
    nodes.forEach((node, index) => {
      if (!node || typeof node !== 'object') {
        this.errors.push(`Node ${index}: Invalid node object`);
        return;
      }

      if (!node.id || typeof node.id !== 'string') {
        this.errors.push(`Node ${index}: Missing or invalid ID`);
        return;
      }

      if (nodeIds.has(node.id)) {
        this.errors.push(`Node ${index}: Duplicate ID "${node.id}"`);
      }
      nodeIds.add(node.id);

      if (!node.type || typeof node.type !== 'string') {
        this.errors.push(`Node ${index}: Missing or invalid type`);
      }
    });
  }

  _detectCycles(nodes, edges) {
    const nodeIds = new Set(nodes.map(n => n?.id).filter(Boolean));
    const adjacencyList = new Map();
    
    // Build adjacency list
    nodes.forEach(node => {
      if (node?.id) {
        adjacencyList.set(node.id, []);
      }
    });
    
    edges.forEach(edge => {
      if (edge.source && edge.target && adjacencyList.has(edge.source)) {
        adjacencyList.get(edge.source).push(edge.target);
      }
    });

    // DFS to detect cycles
    const visited = new Set();
    const recStack = new Set();

    const hasCycle = (nodeId) => {
      if (recStack.has(nodeId)) {
        return true; // Cycle detected
      }
      if (visited.has(nodeId)) {
        return false;
      }

      visited.add(nodeId);
      recStack.add(nodeId);

      const neighbors = adjacencyList.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (hasCycle(neighbor)) {
          return true;
        }
      }

      recStack.delete(nodeId);
      return false;
    };

    for (const nodeId of nodeIds) {
      if (!visited.has(nodeId)) {
        if (hasCycle(nodeId)) {
          this.errors.push(`Cycle detected in flow graph`);
          this.warnings.push(`Potential infinite loop detected`);
          break;
        }
      }
    }
  }

  _findUnreachableNodes(nodes, edges) {
    const nodeIds = new Set(nodes.map(n => n?.id).filter(Boolean));
    const hasIncoming = new Set(edges.map(e => e.target).filter(Boolean));
    
    // Find nodes with no incoming edges (start nodes)
    const startNodes = nodes.filter(n => !hasIncoming.has(n.id));
    
    if (startNodes.length === 0 && nodes.length > 0) {
      this.warnings.push('No start nodes found (nodes with no incoming edges)');
    }

    // Find nodes that are unreachable from start nodes
    if (startNodes.length > 0) {
      const reachable = new Set();
      const queue = [...startNodes.map(n => n.id)];

      while (queue.length > 0) {
        const current = queue.shift();
        if (reachable.has(current)) continue;
        
        reachable.add(current);
        
        // Add all neighbors
        edges
          .filter(e => e.source === current)
          .forEach(e => {
            if (!reachable.has(e.target)) {
              queue.push(e.target);
            }
          });
      }

      const unreachable = nodes.filter(n => !reachable.has(n.id));
      if (unreachable.length > 0) {
        this.warnings.push(
          `Found ${unreachable.length} unreachable node(s): ${unreachable.map(n => n.id).join(', ')}`
        );
      }
    }
  }

  _validateNodeConfigurations(nodes) {
    nodes.forEach((node, index) => {
      if (!node || !node.data || !node.data.config) {
        return; // Skip validation if no config
      }

      const config = node.data.config;

      switch (node.type) {
        case 'message':
        case 'enhancedMessage':
          if (!config.message || typeof config.message !== 'string') {
            this.warnings.push(`Node ${node.id}: Message node missing message text`);
          }
          break;

        case 'button':
        case 'advancedButton':
          if (!config.buttons || !Array.isArray(config.buttons) || config.buttons.length === 0) {
            this.warnings.push(`Node ${node.id}: Button node missing buttons`);
          }
          break;

        case 'condition':
          if (!config.condition || typeof config.condition !== 'string') {
            this.warnings.push(`Node ${node.id}: Condition node has no expression — will always evaluate false`);
          }
          break;

        case 'setVariable':
          if (!config.variableName || typeof config.variableName !== 'string') {
            this.warnings.push(`Node ${node.id}: SetVariable node has no variable name set`);
          }
          break;

        case 'apiCall':
          if (!config.url || typeof config.url !== 'string') {
            this.warnings.push(`Node ${node.id}: API Call node has no URL set`);
          }
          break;
      }
    });
  }

  _validateEdges(nodes, edges) {
    const nodeIds = new Set(nodes.map(n => n?.id).filter(Boolean));

    edges.forEach((edge, index) => {
      if (!edge.source || !edge.target) {
        this.errors.push(`Edge ${index}: Missing source or target`);
        return;
      }

      if (!nodeIds.has(edge.source)) {
        this.errors.push(`Edge ${index}: Source node "${edge.source}" not found`);
      }

      if (!nodeIds.has(edge.target)) {
        this.errors.push(`Edge ${index}: Target node "${edge.target}" not found`);
      }
    });
  }

  getResult() {
    return {
      valid: this.errors.length === 0,
      errors: [...this.errors],
      warnings: [...this.warnings]
    };
  }
}

export default FlowValidator;

